import { useState, useRef, useEffect } from 'react';
import type { Lang } from '../page';

interface NavbarProps {
  isScrolled: boolean;
  lang: Lang;
  onSetLang: (lang: Lang) => void;
}

const languages: { code: Lang; label: string; short: string }[] = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'zh', label: '中文', short: '中文' },
  { code: 'th', label: 'ภาษาไทย', short: 'ไทย' },
  { code: 'vi', label: 'Tiếng Việt', short: 'Việt' },
  { code: 'ms', label: 'Bahasa Melayu', short: 'Melayu' },
];

const navLinkNames: Record<string, Record<Lang, string>> = {
  features: { en: 'Features', zh: '产品优势', th: 'คุณสมบัติ', vi: 'Tính Năng', ms: 'Ciri-ciri' },
  specs: { en: 'Specifications', zh: '技术参数', th: 'ข้อมูลจำเพาะ', vi: 'Thông Số', ms: 'Spesifikasi' },
  applications: { en: 'Applications', zh: '应用领域', th: 'การใช้งาน', vi: 'Ứng Dụng', ms: 'Aplikasi' },
  faq: { en: 'FAQ', zh: '常见问题', th: 'คำถามพบบ่อย', vi: 'FAQ', ms: 'Soalan Lazim' },
};

const contactLabel: Record<Lang, string> = {
  en: 'Contact Us', zh: '联系我们', th: 'ติดต่อเรา', vi: 'Liên Hệ', ms: 'Hubungi Kami',
};

const searchPlaceholder: Record<Lang, string> = {
  en: 'Search products, specifications...',
  zh: '搜索产品、规格...',
  th: 'ค้นหาผลิตภัณฑ์ ข้อมูลจำเพาะ...',
  vi: 'Tìm kiếm sản phẩm, thông số...',
  ms: 'Cari produk, spesifikasi...',
};

const switchLabel: Record<Lang, string> = {
  en: 'Switch Language', zh: '切换语言', th: 'เปลี่ยนภาษา', vi: 'Đổi Ngôn Ngữ', ms: 'Tukar Bahasa',
};

export default function Navbar({ isScrolled, lang, onSetLang }: NavbarProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { key: 'features', href: '#features' },
    { key: 'specs', href: '#specs' },
    { key: 'applications', href: '#applications' },
    { key: 'faq', href: '#faq' },
  ];

  const currentLang = languages.find((l) => l.code === lang) ?? languages[0];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-2">
            <img
              src="https://public.readdy.ai/ai/img_res/2bd46c8d-4232-4ca7-9f70-77346d902090.png"
              alt="Logo"
              className="h-10 w-auto object-contain"
            />
            <span className={`text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              Polyimide Film
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className={`font-medium transition-colors hover:text-amber-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
              >
                {navLinkNames[link.key][lang]}
              </a>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-3">
            {/* Search */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled ? 'hover:bg-gray-100 text-gray-900' : 'hover:bg-white/10 text-white'
              }`}
              aria-label="Search"
            >
              <i className="ri-search-line text-xl"></i>
            </button>

            {/* Language Dropdown */}
            <div ref={langRef} className="hidden lg:block relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg font-medium transition-colors ${
                  isScrolled ? 'hover:bg-gray-100 text-gray-900' : 'hover:bg-white/10 text-white'
                }`}
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm">{currentLang.short}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isLangOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[160px] z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { onSetLang(l.code); setIsLangOpen(false); }}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-gray-50 transition-colors cursor-pointer ${
                        lang === l.code ? 'text-amber-500 font-semibold bg-amber-50' : 'text-gray-700'
                      }`}
                    >
                      <span>{l.label}</span>
                      {lang === l.code && <i className="ri-check-line text-amber-500"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Contact Button */}
            <a
              href="#purchase"
              className="hidden lg:inline-flex items-center space-x-2 px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line"></i>
              <span>{contactLabel[lang]}</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled ? 'hover:bg-gray-100 text-gray-900' : 'hover:bg-white/10 text-white'
              }`}
              aria-label="Menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {isSearchOpen && (
          <div className="py-4 border-t border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder={searchPlaceholder[lang]}
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                autoFocus
              />
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 bg-white">
            <div className="space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  className="block px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {navLinkNames[link.key][lang]}
                </a>
              ))}
              {/* Mobile Language Selector */}
              <div className="px-4 py-3">
                <div className="text-xs text-gray-500 mb-2">{switchLabel[lang]}</div>
                <div className="grid grid-cols-3 gap-2">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { onSetLang(l.code); setIsMobileMenuOpen(false); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        lang === l.code ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {l.short}
                    </button>
                  ))}
                </div>
              </div>
              <a
                href="#purchase"
                className="block px-4 py-3 bg-amber-500 text-white text-center rounded-lg font-semibold hover:bg-amber-600 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {contactLabel[lang]}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
